import React, { useState } from 'react';
import TeachingTooltip from '@/components/TeachingTooltip';

// ─── Sequence color constants ─────────────────────────────────────────────────
const C = { pos: '#0d9488', neg: '#ea580c', zer: '#7c3aed', dim: '#64748b' };

// ─── SVG Circuit dimensions ───────────────────────────────────────────────────
const VW = 140, VH = 178, CX = 70;

// Y-positions along the vertical circuit
const Y = {
  busText: 9, bus: 19,
  ztTop: 30,  ztBot: 50,
  txTop: 58,  txMid: 68, txBot: 82,
  zsTop: 90,  zsBot: 110,
  srcMid: 128,
  gnd: 155,
};

// ─── SVG atoms ────────────────────────────────────────────────────────────────
const GroundSym = ({ c = C.dim }) => (
  <g>
    <line x1={CX - 12} y1={Y.gnd}      x2={CX + 12} y2={Y.gnd}      stroke={c} strokeWidth={1.8} />
    <line x1={CX - 8}  y1={Y.gnd + 5}  x2={CX + 8}  y2={Y.gnd + 5}  stroke={c} strokeWidth={1.8} />
    <line x1={CX - 4}  y1={Y.gnd + 10} x2={CX + 4}  y2={Y.gnd + 10} stroke={c} strokeWidth={1.8} />
  </g>
);

const ZBox = ({ yTop, h = 20, label, col, dim = false }) => (
  <g>
    <rect
      x={CX - 24} y={yTop} width={48} height={h} rx={3}
      fill={dim ? 'rgba(100,116,139,0.07)' : 'rgba(148,163,184,0.18)'}
      stroke={dim ? C.dim : col} strokeWidth={1.5}
      strokeDasharray={dim ? '3 2' : undefined}
    />
    <text
      x={CX} y={yTop + h / 2 + 3.5}
      textAnchor="middle" fontSize="9.5" fontWeight="700"
      fontFamily="IBM Plex Mono, monospace"
      fill={dim ? C.dim : col}
    >{label}</text>
  </g>
);

const VSrc = ({ col }) => (
  <g>
    <circle cx={CX} cy={Y.srcMid} r={14} fill="none" stroke={col} strokeWidth={1.5} />
    <path
      d={`M${CX - 7},${Y.srcMid} q3.5,-7 7,0 q3.5,7 7,0`}
      fill="none" stroke={col} strokeWidth={1.3}
    />
    <text x={CX} y={Y.srcMid - 18} textAnchor="middle" fontSize="8" fill={col} fontWeight="600">Vf</text>
  </g>
);

const OpenSym = ({ yMid, col = C.dim }) => (
  <g>
    <line x1={CX - 13} y1={yMid - 3} x2={CX + 13} y2={yMid - 3} stroke={col} strokeWidth={2} />
    <line x1={CX - 13} y1={yMid + 3} x2={CX + 13} y2={yMid + 3} stroke={col} strokeWidth={2} />
    <text x={CX + 16} y={yMid + 4} fontSize="7.5" fill={col} fontStyle="italic">open</text>
  </g>
);

const DeltaBlock = ({ col = C.dim }) => (
  <g>
    <polygon
      points={`${CX},${Y.txMid - 10} ${CX - 11},${Y.txMid + 6} ${CX + 11},${Y.txMid + 6}`}
      fill="none" stroke={col} strokeWidth={1.5}
    />
    <text x={CX} y={Y.txMid + 20} textAnchor="middle" fontSize="7.5" fill={col}>
      {'\u0394'} blocks I\u2080
    </text>
  </g>
);

const Resistor3n = ({ col }) => (
  <g>
    <rect x={CX - 10} y={Y.srcMid - 8} width={20} height={16} rx={2}
      fill="none" stroke={col} strokeWidth={1.5} />
    <text x={CX} y={Y.srcMid + 3} textAnchor="middle" fontSize="8"
      fontWeight="700" fontFamily="IBM Plex Mono, monospace" fill={col}>3Rn</text>
  </g>
);

// ─── Zero-sequence topology logic ─────────────────────────────────────────────
const getZ0Topo = (txConn, grounding) => {
  const grounded  = grounding !== 'UNGROUNDED';
  const resistive = grounding === 'RESISTANCE';
  // txBlock = transformer connection prevents zero-seq from linking source to fault bus
  const txBlock =
    txConn === 'DYN11' ||  // HV delta blocks source-side zero-seq
    txConn === 'DD0'   ||  // both delta
    txConn === 'YND1';     // LV delta blocks load-side zero-seq
  const srcOpen = !grounded;
  return { txBlock, srcOpen, resistive };
};

// ─── Per-sequence vertical circuit SVG ────────────────────────────────────────
const CircuitSVG = ({ seq, txConn, grounding }) => {
  const isPos  = seq === 'positive';
  const isZero = seq === 'zero';
  const col    = isPos ? C.pos : isZero ? C.zer : C.neg;

  const { txBlock, srcOpen, resistive } = isZero
    ? getZ0Topo(txConn, grounding)
    : { txBlock: false, srcOpen: false, resistive: false };

  const noSrcPath = isZero && srcOpen;
  const noTxPath  = isZero && txBlock;
  const dim       = (b) => (b ? C.dim : col);
  const dash      = (b) => (b ? '4 2' : undefined);
  const op        = (b) => (b ? 0.4  : 1);

  const zSrcLabel = isPos ? 'Z\u2081s' : isZero ? 'Z\u2080s' : 'Z\u2082s';

  // Conductor from Z_src down to source area
  const srcToGndBlocked = noTxPath || noSrcPath;

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full" style={{ maxHeight: '178px' }}>
      {/* ── Fault Bus ──── */}
      <line x1={CX - 28} y1={Y.bus} x2={CX + 28} y2={Y.bus}
        stroke={col} strokeWidth={2.8} strokeLinecap="round" />
      <text x={CX} y={Y.busText} textAnchor="middle" fontSize="7.5"
        fill={col} fontWeight="700" fontFamily="IBM Plex Mono, monospace">FAULT BUS</text>

      {/* ── Bus → Z_T ──── */}
      <line x1={CX} y1={Y.bus} x2={CX} y2={Y.ztTop} stroke={col} strokeWidth={1.6} />

      {/* ── Z_T box ──── */}
      <ZBox yTop={Y.ztTop} label="Z_T" col={col} />

      {/* ── Z_T → TX area ──── */}
      <line x1={CX} y1={Y.ztBot} x2={CX} y2={Y.txTop}
        stroke={dim(noTxPath)} strokeWidth={1.6}
        opacity={op(noTxPath)} strokeDasharray={dash(noTxPath)} />

      {/* ── TX connection symbol ──── */}
      {noTxPath ? (
        <DeltaBlock col={C.dim} />
      ) : (
        <line x1={CX} y1={Y.txTop} x2={CX} y2={Y.txBot}
          stroke={col} strokeWidth={1.6} />
      )}

      {/* ── TX → Z_src ──── */}
      <line x1={CX} y1={Y.txBot} x2={CX} y2={Y.zsTop}
        stroke={dim(srcToGndBlocked)} strokeWidth={1.6}
        opacity={op(srcToGndBlocked)} strokeDasharray={dash(srcToGndBlocked)} />

      {/* ── Z_src box ──── */}
      <ZBox yTop={Y.zsTop} label={zSrcLabel} col={col} dim={srcToGndBlocked} />

      {/* ── Z_src → Source ──── */}
      <line x1={CX} y1={Y.zsBot} x2={CX} y2={Y.srcMid - 16}
        stroke={dim(noSrcPath)} strokeWidth={1.6}
        opacity={op(noSrcPath)} strokeDasharray={dash(noSrcPath)} />

      {/* ── Source / terminal symbol ──── */}
      {isZero && noSrcPath ? (
        <OpenSym yMid={Y.srcMid} col={C.dim} />
      ) : isZero && resistive ? (
        <g>
          <Resistor3n col={col} />
          <line x1={CX} y1={Y.srcMid + 8} x2={CX} y2={Y.gnd} stroke={col} strokeWidth={1.6} />
          <GroundSym c={col} />
        </g>
      ) : isZero ? (
        <g>
          <line x1={CX} y1={Y.srcMid - 16} x2={CX} y2={Y.gnd} stroke={col} strokeWidth={1.6} />
          <GroundSym c={col} />
        </g>
      ) : isPos ? (
        <g>
          <VSrc col={col} />
          <line x1={CX} y1={Y.srcMid + 14} x2={CX} y2={Y.gnd} stroke={col} strokeWidth={1.6} />
          <GroundSym c={C.dim} />
        </g>
      ) : (
        // Negative: 0V
        <g>
          <text x={CX} y={Y.srcMid - 4} textAnchor="middle" fontSize="9" fill={col} fontWeight="600">0V</text>
          <line x1={CX - 9} y1={Y.srcMid + 3} x2={CX + 9} y2={Y.srcMid + 3} stroke={col} strokeWidth={2} />
          <line x1={CX} y1={Y.srcMid + 3} x2={CX} y2={Y.gnd} stroke={col} strokeWidth={1.6} />
          <GroundSym c={C.dim} />
        </g>
      )}
    </svg>
  );
};

// ─── Panel wrapper ────────────────────────────────────────────────────────────
const SEQ_META = {
  positive: { label: 'Positive (Z\u2081)',  note: 'ABC rotation \u2014 source V\u2080 drives fault current', col: C.pos, bg: 'bg-teal-50/60  dark:bg-teal-950/30',  border: 'border-teal-200  dark:border-teal-800' },
  negative: { label: 'Negative (Z\u2082)',  note: 'ACB rotation \u2014 no source voltage, Z\u2082\u2248Z\u2081',  col: C.neg, bg: 'bg-orange-50/60 dark:bg-orange-950/20', border: 'border-orange-200 dark:border-orange-800' },
  zero:     { label: 'Zero (Z\u2080)',      note: 'Cophasal \u2014 topology depends on grounding & TX connection', col: C.zer, bg: 'bg-purple-50/60 dark:bg-purple-950/20', border: 'border-purple-200 dark:border-purple-800' },
};

// ─── Dynamic callout lookup ───────────────────────────────────────────────────
const CALLOUTS = {
  'DYN11-SOLID':
    'Delta-Wye (Dyn11) solidly grounded: the HV delta winding blocks zero-sequence current from the source side — no upstream contribution. The grounded LV neutral provides a local zero-sequence return path through Z_T and the neutral to ground. Z\u2080 seen from the LV fault bus = Z_T only. This is the most common distribution transformer in North America, and it explains why SLG fault current on a Dyn feeder is limited by transformer impedance, not the source.',
  'DYN11-RESISTANCE':
    'Delta-Wye (Dyn11) with resistance grounding: the HV delta still blocks the source side zero-sequence path. On the LV side, zero-sequence current flows through Z_T and the neutral resistor Rn. The neutral resistor appears as 3Rn in the zero-sequence circuit (not Rn), because zero-sequence current flows equally in all three phases simultaneously. Ground fault current is limited to Vf / (Z_T + 3Rn).',
  'DYN11-UNGROUNDED':
    'Delta-Wye (Dyn11) with ungrounded LV: the HV delta blocks the source side AND the floating LV neutral provides no ground return path. The zero-sequence network is completely open on both sides. No SLG fault current flows. Ground faults on this system are undetectable with standard overcurrent relays \u2014 a neutral grounding transformer or a neutral monitoring relay must be added.',
  'YNYN0-SOLID':
    'Wye-Wye (YNyn0) solidly grounded: zero-sequence current has a complete path through both the HV and LV grounded neutrals. Z\u2080_Th = Z\u2080_source + Z_T. The HV neutral carries 3I\u2080 back to the source, which is why the HV side relay also sees ground fault current. Both sequence networks are fully connected \u2014 the simplest zero-sequence topology.',
  'YNYN0-RESISTANCE':
    'Wye-Wye (YNyn0) with resistance grounding: zero-sequence current flows on both sides but is limited by the neutral resistors. Each resistor appears as 3Rn in the zero-sequence circuit. Z\u2080_Th = Z\u2080_source + 3Rn_HV + Z_T + 3Rn_LV. Ground fault current is limited, which reduces damage but may complicate relay coordination.',
  'YNYN0-UNGROUNDED':
    'Wye-Wye (YNyn0) with ungrounded neutrals: despite the wye windings, there is no neutral-to-ground connection. The zero-sequence network is completely open. SLG faults are undetectable \u2014 a frequent and dangerous protection gap in older industrial systems.',
  'DD0-SOLID':
    'Delta-Delta (Dd0): both windings are delta \u2014 there are no neutral points on either side. Zero-sequence current cannot flow in line conductors on either side, regardless of any external grounding. The zero-sequence network is permanently open on both sides. This transformer configuration completely isolates zero-sequence between the HV and LV systems. Zero-sequence current from a ground fault on one side cannot reach the other side at all.',
  'DD0-RESISTANCE':
    'Delta-Delta (Dd0) with resistance grounding: the grounding setting has no effect because there are no wye neutral points to connect a grounding resistor to. Both delta windings inherently block zero-sequence current in the line conductors. The zero-sequence network remains open on both sides.',
  'DD0-UNGROUNDED':
    'Delta-Delta (Dd0): identical result to any grounding choice. There are no neutral points, so grounding type is irrelevant. Zero-sequence network is open on both sides. Zero-sequence faults on either side do not produce fault current through this transformer.',
  'YND1-SOLID':
    'Wye-Delta (YNd1) solidly grounded: the grounded-wye HV side provides a zero-sequence return path from the source. However, the delta LV winding blocks zero-sequence from the load side \u2014 zero-sequence current circulates inside the delta but cannot exit into the LV conductors. This is useful for isolating ground faults: an SLG fault on the LV system cannot propagate zero-sequence current to the HV system.',
  'YND1-RESISTANCE':
    'Wye-Delta (YNd1) with resistance grounding: the grounded-wye HV (through Rn) limits zero-sequence from the source side. The delta LV still completely blocks zero-sequence on the load side. Ground fault current magnitude is set by Vf / (Z\u2080s + 3Rn), not by the load-side impedance.',
  'YND1-UNGROUNDED':
    'Wye-Delta (YNd1) with ungrounded wye: the ungrounded HV neutral provides no zero-sequence return path. The LV delta already blocks zero-sequence on its side. Result: complete open circuit in the zero-sequence network on both sides.',
};

// ─── Dropdown options ─────────────────────────────────────────────────────────
const TX_OPTIONS = [
  { value: 'DYN11', label: 'Delta-Wye (Dyn11)' },
  { value: 'YNYN0', label: 'Wye-Wye (YNyn0)' },
  { value: 'DD0',   label: 'Delta-Delta (Dd0)' },
  { value: 'YND1',  label: 'Wye-Delta (YNd1)' },
];
const GND_OPTIONS = [
  { value: 'SOLID',      label: 'Solidly Grounded' },
  { value: 'RESISTANCE', label: 'Resistance Grounded' },
  { value: 'UNGROUNDED', label: 'Ungrounded' },
];

// ─── Main component ───────────────────────────────────────────────────────────
const SequenceNetworkBuilder = () => {
  const [txConn,   setTx]  = useState('DYN11');
  const [grounding, setGnd] = useState('SOLID');
  const calloutKey  = `${txConn}-${grounding}`;
  const calloutText = CALLOUTS[calloutKey] || '';

  return (
    <div data-testid="sequence-network-builder" className="space-y-5">
      {/* ── Dropdown controls ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Transformer Connection */}
        <div>
          <TeachingTooltip
            title="Transformer Connection"
            whatThis="The transformer winding configuration (delta or wye on each side). Determines whether a neutral point exists and how zero-sequence current flows through the transformer."
            tryThis="Select Delta-Wye (Dyn11) first — this is the most common distribution feeder transformer in North America. Then switch to Wye-Wye (YNyn0) to see how the zero-sequence network changes."
            whyMatters="Delta windings have no neutral point and block zero-sequence current in line conductors. Grounded-wye windings provide a zero-sequence return path. This single choice determines whether SLG fault current can flow."
            connectedTo="Zero-sequence circuit diagram (purple panel), dynamic explanation callout below"
          >
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5 uppercase tracking-wide">
                (1) Transformer Connection
              </label>
              <select
                data-testid="tx-connection-select"
                value={txConn}
                onChange={(e) => setTx(e.target.value)}
                className="w-full text-sm border border-slate-300 dark:border-slate-600 rounded-md px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                {TX_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </TeachingTooltip>
        </div>

        {/* System Grounding */}
        <div>
          <TeachingTooltip
            title="System Grounding"
            whatThis="How the neutral point of a wye winding is connected to earth. Solidly grounded = direct wire to ground (Zn = 0). Resistance grounded = neutral through a resistor (Rn). Ungrounded = neutral floating, no earth connection."
            tryThis="Toggle between Solidly Grounded and Ungrounded to see the zero-sequence source symbol switch between a direct ground connection and an open circuit."
            whyMatters="Grounding determines whether zero-sequence current can return to source after a ground fault. Ungrounded systems have zero-sequence open circuits and cannot detect SLG faults with standard relays."
            connectedTo="Zero-sequence circuit source symbol, dynamic explanation callout below"
          >
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5 uppercase tracking-wide">
                (2) System Grounding
              </label>
              <select
                data-testid="grounding-select"
                value={grounding}
                onChange={(e) => setGnd(e.target.value)}
                className="w-full text-sm border border-slate-300 dark:border-slate-600 rounded-md px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                {GND_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </TeachingTooltip>
        </div>
      </div>

      {/* ── Three circuit diagrams ── */}
      <div className="grid grid-cols-3 gap-3" data-testid="sequence-circuit-panels">
        {(['positive', 'negative', 'zero'] ).map((seq) => {
          const meta = SEQ_META[seq];
          return (
            <div
              key={seq}
              data-testid={`circuit-panel-${seq}`}
              className={`rounded-xl border ${meta.border} ${meta.bg} p-3 flex flex-col items-center`}
            >
              <p className="text-xs font-semibold mb-1 font-mono" style={{ color: meta.col }}>
                {meta.label}
              </p>
              <p className="text-[10px] text-slate-500 dark:text-slate-500 text-center mb-2 leading-tight">
                {meta.note}
              </p>
              <CircuitSVG seq={seq} txConn={txConn} grounding={grounding} />
            </div>
          );
        })}
      </div>

      {/* ── Dynamic explanation callout ── */}
      <div
        data-testid="sequence-network-callout"
        className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-4"
      >
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-widest mb-2">
          What this configuration means
        </p>
        <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
          {calloutText}
        </p>
      </div>
    </div>
  );
};

export default SequenceNetworkBuilder;
